"use client";

import React from "react";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { reviewsFor, type CanvasReview } from "./canvas-reviews";
import Marquee from "./Marquee";

export interface ReviewsMarqueeProps {
  /** Market locale. Decides which verified set is shown, if any. */
  locale: string;
  /** Smaller cards and no top margin, for the strip under a checkout button. */
  compact?: boolean;
}

/** Seconds for one full pass. Slow enough to read a card as it goes by. */
const DURATION_S = 34;

function ReviewCard({ review, compact }: { review: CanvasReview; compact?: boolean }) {
  return (
    <Stack
      direction="row"
      spacing={compact ? 1 : 1.5}
      // One size for every card. Sized by its text, a two-line comment sat
      // beside a four-line one and the strip read as a ragged row of boxes.
      // Longer comments are cut to the same number of lines.
      sx={(theme) => ({
        width: compact ? 240 : 290,
        height: compact ? 104 : 132,
        boxSizing: "border-box",
        overflow: "hidden",
        flex: "0 0 auto",
        p: compact ? 1.25 : 2,
        borderRadius: 3,
        alignItems: "flex-start",
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      })}
    >
      <Avatar
        sx={(theme) => ({
          width: compact ? 28 : 36,
          height: compact ? 28 : 36,
          fontSize: compact ? 12 : 14,
          fontWeight: 700,
          bgcolor: theme.palette.primary.main,
        })}
      >
        {review.initials}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: compact ? 12.5 : undefined }}
        >
          {review.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            ...(compact ? { fontSize: 12, lineHeight: 1.4 } : {}),
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: compact ? 3 : 4,
            overflow: "hidden",
          }}
        >
          {review.text}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * The reassurance strip under the buy button: real Facebook comments, moving.
 *
 * It sits where the hesitation is. Everything above the button is the shop
 * talking about its own printing; this is the only place on the page where
 * somebody else vouches for it, and the customer meets it at the moment they
 * decide whether to pay.
 *
 * Motion earns its place by fitting three cards' worth of proof into one
 * card's height — but it never traps the reader: hovering stops the track,
 * and anyone who has asked their system to reduce motion gets a still,
 * swipeable row instead.
 *
 * Markets with no verified comments render nothing at all. An empty space is
 * honest; a borrowed or invented quote under a price is not.
 */
export default function ReviewsMarquee({ locale, compact }: ReviewsMarqueeProps) {
  const set = reviewsFor(locale);
  if (!set) return null;

  return (
    <Box sx={compact ? { mb: 1.5 } : { mt: 3 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{
          display: "block",
          textAlign: "center",
          letterSpacing: 1,
          ...(compact && { fontSize: 10.5, lineHeight: 2 }),
        }}
      >
        {set.title}
      </Typography>

      <Box sx={{ mt: compact ? 0.5 : 1 }}>
        <Marquee
          durationS={DURATION_S}
          gap={2}
          fade={8}
          items={set.items.map((review) => (
            <ReviewCard key={review.initials + review.name} review={review} compact={compact} />
          ))}
        />
      </Box>
    </Box>
  );
}
